import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, ShieldAlert, Sparkles } from 'lucide-react';
import { Field, Input, Textarea, Checkbox } from '../../components/ui/Field';
import ColorPickerInput from '../../components/ui/ColorPickerInput';
import { api } from '../../lib/api';
import { panelMeta } from '../../lib/panelMeta';
import type { ChatbotSettingsInput, ChatbotSettingsRecord } from '../../lib/types';
import { chatbotFade } from './chatbotShared';

export default function ChatbotConfigTab() {
  const [form, setForm] = useState<ChatbotSettingsRecord | null>(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    api.chatbot.getSettings().then(setForm).catch(() => setError('No se pudo cargar la configuración. Actualiza la página.'));
  }, []);

  function set<K extends keyof ChatbotSettingsRecord>(key: K, value: ChatbotSettingsRecord[K]) {
    setForm((current) => (current ? { ...current, [key]: value } : current));
  }

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!form) return;
    setSaving(true);
    setSuccess('');
    setError('');
    try {
      const payload: ChatbotSettingsInput = {
        enabled: form.enabled,
        model: form.model,
        systemPrompt: form.systemPrompt,
        persona: form.persona,
        temperature: form.temperature,
        dailyMessageLimit: form.dailyMessageLimit,
        visitorDailyLimit: form.visitorDailyLimit,
        leadCaptureEnabled: form.leadCaptureEnabled,
        accentColor: form.accentColor,
        greeting: form.greeting,
        fallback: form.fallback,
      };
      await api.chatbot.updateSettings(payload);
      setSuccess('Cambios guardados. Recuerda publicar tu sitio para que el asistente aparezca actualizado.');
    } catch {
      setError('No se pudieron guardar los cambios. Inténtalo de nuevo.');
    } finally {
      setSaving(false);
    }
  }

  if (!form) {
    return <div className="flex items-center gap-2 text-sm text-ui-text-subtle"><Loader2 size={15} className="animate-spin" /> Cargando…</div>;
  }

  const numOrNull = (value: string): number | null => {
    const numeric = Number(value);
    return value.trim() === '' || !Number.isFinite(numeric) ? null : Math.max(0, Math.round(numeric));
  };

  return (
    <motion.form {...chatbotFade} onSubmit={save} className="space-y-6">
      {!form.aiConfigured && (
        <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-sm text-ui-text-muted">
          <ShieldAlert size={16} className="mt-0.5 text-amber-400 flex-shrink-0" />
          <span>El asistente aún no está conectado a su motor de respuestas. Mientras tanto puedes configurarlo, pero no responderá a los visitantes.</span>
        </div>
      )}

      <div className="rounded-lg border border-ui-border bg-ui-surface p-4">
        <Checkbox
          label={<span className="text-sm text-ui-text">Mostrar el asistente en mi sitio</span>}
          checked={form.enabled}
          onChange={(event) => set('enabled', event.target.checked)}
        />
        <p className="mt-1.5 ml-6 text-sm text-ui-text-subtle">
          Cuando está activo, aparece una burbuja de chat en todas las páginas de tu sitio.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Nombre del asistente" hint="Cómo se presenta ante tus visitantes.">
          <Input
            value={form.persona}
            placeholder={panelMeta.assistantDefaultName}
            onChange={(event) => set('persona', event.target.value)}
          />
        </Field>
        <Field label="Color de la burbuja" hint="Déjalo vacío para usar el color de tu marca.">
          <ColorPickerInput value={form.accentColor ?? ''} onChange={(value) => set('accentColor', value)} />
        </Field>
      </div>

      <Field label="Saludo inicial (español)" hint="El primer mensaje que verá el visitante al abrir el chat.">
        <Input value={form.greeting.es} placeholder="¡Hola! ¿En qué puedo ayudarte?" onChange={(event) => set('greeting', { ...form.greeting, es: event.target.value })} />
      </Field>
      <Field label="Saludo inicial (inglés)">
        <Input value={form.greeting.en} placeholder="Hi! How can I help you?" onChange={(event) => set('greeting', { ...form.greeting, en: event.target.value })} />
      </Field>

      <Field
        label={<span className="flex items-center gap-1.5"><Sparkles size={14} className="text-ui-accent" /> Instrucciones para el asistente</span>}
        hint="Tono y detalles propios de tu negocio. Si lo dejas vacío, usará un estilo cálido y profesional por defecto."
      >
        <Textarea
          rows={4}
          value={form.systemPrompt}
          placeholder="Ej: Habla de usted, destaca que damos asesoría sin compromiso y que atendemos toda la zona metropolitana."
          onChange={(event) => set('systemPrompt', event.target.value)}
        />
      </Field>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Mensaje cuando no sabe la respuesta (español)" hint="Lo usará para invitar a contactarte cuando no encuentre el dato.">
          <Textarea rows={2} value={form.fallback.es} placeholder="No tengo ese dato a la mano, pero el equipo puede ayudarle." onChange={(event) => set('fallback', { ...form.fallback, es: event.target.value })} />
        </Field>
        <Field label="Mensaje cuando no sabe la respuesta (inglés)">
          <Textarea rows={2} value={form.fallback.en} placeholder="I don't have that at hand, but the team can help you." onChange={(event) => set('fallback', { ...form.fallback, en: event.target.value })} />
        </Field>
      </div>

      <div className="rounded-lg border border-ui-border bg-ui-surface p-4 space-y-3">
        <p className="text-sm font-medium text-ui-text">Uso responsable</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Máximo de respuestas por día" hint="En todo tu sitio. Vacío = usar el límite estándar.">
            <Input
              type="number"
              min={0}
              value={form.dailyMessageLimit ?? ''}
              placeholder="200"
              onChange={(event) => set('dailyMessageLimit', numOrNull(event.target.value))}
            />
          </Field>
          <Field label="Máximo por visitante al día" hint="Evita que una sola persona acapare el asistente. Vacío = límite estándar.">
            <Input
              type="number"
              min={0}
              value={form.visitorDailyLimit ?? ''}
              placeholder="30"
              onChange={(event) => set('visitorDailyLimit', numOrNull(event.target.value))}
            />
          </Field>
        </div>
        <Checkbox
          label={<span className="text-sm text-ui-text-muted">Permitir que el asistente recoja los datos de visitantes interesados (aparecen en Consultas)</span>}
          checked={form.leadCaptureEnabled}
          onChange={(event) => set('leadCaptureEnabled', event.target.checked)}
        />
      </div>

      <details className="rounded-lg border border-ui-border bg-ui-surface p-4">
        <summary className="text-sm font-medium text-ui-text cursor-pointer">Opciones avanzadas</summary>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Modelo" hint={`Déjalo vacío para usar el modelo estándar (${form.defaultModel}).`}>
            <Input value={form.model} placeholder={form.defaultModel} onChange={(event) => set('model', event.target.value)} />
          </Field>
          <Field label="Creatividad de las respuestas" hint="0 = muy literal y prudente, 1 = más libre. Recomendado: 0.3.">
            <Input
              type="number"
              min={0}
              max={1}
              step={0.1}
              value={form.temperature}
              onChange={(event) => set('temperature', Math.min(1, Math.max(0, Number(event.target.value) || 0)))}
            />
          </Field>
        </div>
      </details>

      {success && <p className="text-sm text-ui-success">{success}</p>}
      {error && <p className="text-sm text-ui-danger">{error}</p>}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="px-5 py-2 bg-ui-accent hover:bg-ui-accent-hover text-ui-text-inverse text-sm font-medium rounded-md disabled:opacity-50 transition-colors"
        >
          {saving ? 'Guardando…' : 'Guardar cambios'}
        </button>
      </div>
    </motion.form>
  );
}
